// The contact book (screaming.md → contact): shared by the API and the SPA, held to
// docs/openapi.json by contact.test.ts. One body on both doors, so it is snake_case like the
// rest of the v3 contract (created_at, per_page). A contact has no role of its own: a domain
// names one as its registrant, admin, tech or billing contact.
import { z } from "zod";

/** EPP's phone form: +, the country code, a dot, the number (RFC 5733). */
export const PHONE_PATTERN = /^\+\d{1,3}\.\d{4,14}$/;
export const COUNTRY_PATTERN = /^[A-Z]{2}$/;

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => value || null);

export const ContactInput = z.object({
  name: text(255),
  organisation: optionalText(255),
  email: z.email().max(254),
  phone: z.string().trim().regex(PHONE_PATTERN),
  address1: text(255),
  address2: optionalText(255),
  city: text(255),
  state: text(255),
  postcode: text(16),
  country: z.string().trim().regex(COUNTRY_PATTERN),
});
export type ContactInput = z.infer<typeof ContactInput>;

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

export const Contact = ContactInput.extend({ id: Base62Id, created_at: z.iso.datetime() });
export type Contact = z.infer<typeof Contact>;

export const PageMeta = z.object({ page: z.int(), per_page: z.int(), total: z.int() });
export type PageMeta = z.infer<typeof PageMeta>;

export const ContactListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(25),
});
export type ContactListQuery = z.infer<typeof ContactListQuery>;

export const ContactResponse = z.object({ data: Contact });
export const ContactListResponse = z.object({ data: z.array(Contact), meta: PageMeta });
